import {
  formatDate,
  hasArrayElements,
  mapEdgesToItems,
  toFixed,
  toPrecise,
} from "@/apiUtils/commonUtils";
import { lensConfigurationSteps } from "@/apiUtils/constants";
import { getAttributeValue, formatDiscountPercentLabel, getVariantImages } from "@/apiUtils/productUtils";
import {
  AddOnAttributeType,
  AddOnVariantInfo,
  AdditionalOptionsSectionItem,
  BrandBundleRef,
  ChoiceType,
  ConfiguratorProductInfo,
  CustomerPrescriptionNode,
  AppliedPromotionsType,
  CustomerPrescriptions,
  FilterType,
  LCSkuType,
  LensConfigs,
  LensBundleSectionItem,
  LensConfigurationPageData,
  LensConfiguratorNode,
  LensIndexItem,
  LensOptionsSectionItem,
  LensSummaryItemType,
  OpticalData,
  PrescriptionData,
  Product,
  SaleorMetadata,
  TransitionItemType,
  VariantItemPdp,
} from "@/common/lib/types";
import {
  attributeNameProperty,
  attributeValueProperty,
  customFilters,
  customizableProperties,
  defaultFilters,
  disableBusinessRules,
  indexParser,
  lensConfigSteps,
  lensDesignValues,
  lensIndexValue,
  lensPowerAttributeNames,
  lensTypes,
  metadataKeys,
  metadataValues,
  percentileRoundOff,
  priceRoundOff,
  propertySlugs,
  slugPropertyMappings,
  slugSeperator,
  stepTitles,
  summarizableSteps,
  userSteps,
} from "@/components/lcp/resource/config";
import * as Sentry from "@sentry/nextjs";

/**
 * Get Lens Configurations (Applies conditional exclusion of User Steps)
 * @param type
 * @param excludeUserSteps
 * @returns
 */
export const getLensConfigs = (type: string, excludeUserSteps?: boolean) => {
  return excludeUserSteps && type === lensTypes.SINGLE_VISION
    ? lensConfigSteps[type].filter((step) => !userSteps.includes(step))
    : lensConfigSteps[type];
};

/**
 * Populate Filters from step and data
 *
 * @param step
 * @param data
 * @param filters
 * @returns
 */
export const processFilters = (step: string, data: any, filters: Record<string, any>) => {
  if (step === lensConfigurationSteps.LENS_TYPE) {
    return { ...filters, LENS_TYPE: data?.type };
  }
  if (step === lensConfigurationSteps.UPLOAD_PRESCRIPTION) {
    return { ...filters, PRESCRIPTION: data };
  }
  if (step === lensConfigurationSteps.LENS_CUSTOMIZATION) {
    const [DESIGN, INDEX, TREATMENT, COATING] = (data?.slug || "").split(slugSeperator);
    return {
      ...filters,
      CUSTOMIZATION: { custom: false, ID: data?.id, DESIGN, INDEX, TREATMENT, COATING },
    };
  }
  if (step === "ADVANCED_CUSTOMIZATION") {
    return {
      ...filters,
      CUSTOMIZATION: {
        custom: true,
        ...(data?.DESIGN ? { DESIGN: data?.DESIGN } : {}),
        ...(data?.INDEX ? { INDEX: data?.INDEX } : {}),
      },
    };
  }
  return { ...filters, [step]: data };
};

export const resetFilters = (
  step: lensConfigurationSteps,
  filters: Record<string, any>,
): Record<string, any> => {
  let newFilters = { ...filters };
  /**
   * These cases are expected to cascade in this order. *Not a mistake
   */
  switch (step) {
    case lensConfigurationSteps.LENS_TYPE:
      newFilters = { ...newFilters, LENS_TYPE: null };
    case lensConfigurationSteps.PRESCRIPTION:
      newFilters = { ...newFilters, PRESCRIPTION: null };
    case lensConfigurationSteps.UPLOAD_PRESCRIPTION:
      newFilters = { ...newFilters, PRESCRIPTION: null };
    case lensConfigurationSteps.LENS_CUSTOMIZATION:
      newFilters = { ...newFilters, CUSTOMIZATION: null };
    case lensConfigurationSteps.ADDITIONAL_OPTIONS:
      newFilters = { ...newFilters };
      break;
    default:
      break;
  }
  return newFilters;
};

/**
 * Populate summary information for summarizableSteps
 *
 * @param data
 * @param step
 * @returns
 */
export const populateSummary = (
  data: any,
  step: lensConfigurationSteps,
  currentSummary: LensSummaryItemType[],
): LensSummaryItemType[] => {
  if (!summarizableSteps.includes(step)) {
    return [...currentSummary];
  }
  if (step === lensConfigurationSteps.LENS_TYPE) {
    return [
      ...currentSummary,
      {
        key: `${step}`,
        option: stepTitles[step],
        choices: [{ value: data?.type, displayValue: data?.title }],
      },
    ];
  }
  if (step === lensConfigurationSteps.LENS_CUSTOMIZATION) {
    return [
      ...currentSummary,
      {
        key: `${step}`,
        option: stepTitles[step],
        choices: [
          ...(data?.isBundle
            ? [{ key: "DESIGN", value: data?.slug, displayValue: data?.title }]
            : [
                {
                  key: "DESIGN",
                  value: data?.choiceSummary?.DESIGN?.key,
                  displayValue: data?.choiceSummary?.DESIGN?.title,
                },
                {
                  key: "INDEX",
                  value: data?.choiceSummary?.INDEX?.key,
                  displayValue: data?.choiceSummary?.INDEX?.title,
                },
              ]),
        ],
        price: data?.price,
        offerPrice: data?.offerPrice,
        offerPercent: data?.offerPercent,
        tax: data?.tax,
      },
    ];
  }

  if (step === lensConfigurationSteps.ADDITIONAL_OPTIONS) {
    return [
      ...currentSummary,
      {
        key: `${step}`,
        option: stepTitles[step],
        choices: [
          { value: data?.slug, displayValue: data?.title },
          ...(data?.color ? [{ key: "COLOR", value: data?.color, displayValue: data?.name }] : []),
        ],
        price: data?.price === "INCLUDED" ? "Included" : data?.price,
        offerPrice: data?.offerPrice,
        offerPercent: data?.offerPercent,
      },
    ];
  }
  return [];
};

/**
 * Resets the summary based on the step in a cascading manner
 * @param summary
 * @param step
 * @returns
 */
export const resetSummary = (
  summary: LensSummaryItemType[],
  step: lensConfigurationSteps,
): LensSummaryItemType[] => {
  let summaries = [...summary];

  if (summarizableSteps.includes(step)) {
    switch (step) {
      case lensConfigurationSteps.LENS_TYPE:
        summaries = summaries.filter((item) => item.key !== lensConfigurationSteps.LENS_TYPE);
      case lensConfigurationSteps.LENS_CUSTOMIZATION:
        summaries = summaries.filter(
          (item) => item.key !== lensConfigurationSteps.LENS_CUSTOMIZATION,
        );
      case lensConfigurationSteps.ADDITIONAL_OPTIONS:
        summaries = summaries.filter(
          (item) => item.key !== lensConfigurationSteps.ADDITIONAL_OPTIONS,
        );
      default:
        break;
    }
  }
  return [...summaries];
};

/**
 * Parse attributes information from AddOn data
 *
 * @param attributeArray
 * @returns
 */
export const parseAttributes = (attributeArray: AddOnAttributeType[]): string => {
  const attributeMap: Record<string, string> = {
    [customizableProperties.DESIGN]: "",
    [customizableProperties.INDEX]: "",
    [customizableProperties.TREATMENT]: "",
    [customizableProperties.COATING]: "",
  };
  const validKeys = Object.keys(slugPropertyMappings);
  attributeArray.map((a) => {
    if (validKeys.includes(a?.attribute?.[attributeNameProperty] || "")) {
      attributeMap[slugPropertyMappings[a.attribute?.[attributeNameProperty]]] =
        a.values?.[0]?.[attributeValueProperty];
    }
  });

  return `${attributeMap[customizableProperties.DESIGN]}${slugSeperator}${attributeMap[customizableProperties.INDEX]}${slugSeperator}${attributeMap[customizableProperties.TREATMENT]}${slugSeperator}${attributeMap[customizableProperties.COATING]}`;
};

type TaxedMoneySide = { amount?: number | null } | null;

type VariantRrpPricing = {
  costPrice?: { net?: TaxedMoneySide; gross?: TaxedMoneySide } | null;
  priceUndiscounted?: { net?: TaxedMoneySide; gross?: TaxedMoneySide } | null;
} | null;

/** Rounded to 3 decimals (smallest unit of KWD/BHD/OMR) so float artifacts don't register as a difference. */
const toMinorUnits = (amount: number): number => Math.round(amount * 1000);

/**
 * Display list / strikethrough RRP: costPrice when it exceeds catalogue list, else priceUndiscounted.
 * Applied to every variant, not only OL lenses: relies on costPrice exceeding priceUndiscounted
 * only for ERP-markdown SKUs.
 */
export const getVariantRrpAmount = (
  pricing?: VariantRrpPricing | null,
  kind: "gross" | "net" = "gross",
): number => {
  const list = pricing?.priceUndiscounted?.[kind]?.amount ?? 0;
  const cost = pricing?.costPrice?.[kind]?.amount;
  if (typeof cost === "number" && toMinorUnits(cost) > toMinorUnits(list)) {
    return cost;
  }
  return list;
};

/**
 * Parse requried values from AddOn info
 *
 * @param info
 * @returns
 */
export const parseInfo = (
  info: AddOnVariantInfo[],
  opticalData?: OpticalData, // Note: The api is not returning a single variant now properly. So, the opticalData is passed to get the proper sku
): {
  sku: LCSkuType;
  price: { net: number; gross: number };
  discount: { net: number; gross: number };
  undiscounted: { net: number; gross: number };
  tax: { type: string; amount: number };
  discountPercent?: number | null;
  availablePromotions?: AppliedPromotionsType[];
} => {
  // NB: Assumes all variants have the same price and in most scenarios there will
  // be only one variant returned in the response (when without prescription it will be 0 power)

  // NB: *2 is a temporary fix for the price doubling issue , populate the price correctly from sku[0] and sku[1]

  // Since the api is failing to return skus as per previous note, the below code is updated with a workaround to get the proper skus

  const properSkus = parseProperSku(info, opticalData);

  const left = properSkus?.[0];
  const right = properSkus?.[1] || properSkus?.[0];

  if (!left || !right) {
    return {
      sku: { left: null, right: null },
      price: { net: 0, gross: 0 },
      discount: { net: 0, gross: 0 },
      undiscounted: { net: 0, gross: 0 },
      tax: { type: "INCLUSIVE", amount: 0 },
      availablePromotions: [],
    };
  }

  return {
    sku: { left: left || null, right: right || null },
    price: {
      net: left?.pricing?.price?.net?.amount + right?.pricing?.price?.net?.amount,
      gross: left?.pricing?.price?.gross?.amount + right?.pricing?.price?.gross?.amount,
    },
    discount: {
      net: left?.pricing?.discount?.net?.amount + right?.pricing?.discount?.net?.amount,
      gross: left?.pricing?.discount?.gross?.amount + right?.pricing?.discount?.gross?.amount,
    },
    undiscounted: {
      net: getVariantRrpAmount(left?.pricing, "net") + getVariantRrpAmount(right?.pricing, "net"),
      gross:
        getVariantRrpAmount(left?.pricing, "gross") + getVariantRrpAmount(right?.pricing, "gross"),
    },
    tax: {
      type: "INCLUSIVE",
      amount: left?.pricing?.price?.tax?.amount + right?.pricing?.price?.tax?.amount,
    },
    discountPercent:
      left?.pricing?.discountPercent !== undefined
        ? left.pricing.discountPercent
        : right?.pricing?.discountPercent,
    availablePromotions: left?.availablePromotions?.length
      ? left.availablePromotions
      : right?.availablePromotions || [],
  };
};

const parsePowerOptions = (powerString?: string) => {
  return powerString ? parseFloat(toFixed(parseFloat(powerString), 2)) : 0;
};

const parseProperSku = (variants: AddOnVariantInfo[], opticalData?: OpticalData) => {
  const left = {
    sphere: parsePowerOptions(opticalData?.left?.sphericalPower ?? "0"),
    cylinder: parsePowerOptions(opticalData?.left?.cylinderPower ?? "0"),
  };
  const right = {
    sphere: parsePowerOptions(opticalData?.right?.sphericalPower ?? "0"),
    cylinder: parsePowerOptions(opticalData?.right?.cylinderPower ?? "0"),
  };
  return variants.filter((variant) => {
    const linderAttribute = variant.attributes.find((attr) => attr.attribute.slug === "linder");
    const sphereAttribute = variant.attributes.find((attr) => attr.attribute.slug === "sphere");
    // Check if both linder and sphere exist and their first values meet the conditions
    const linder = parsePowerOptions(linderAttribute?.values?.[0]?.name);
    const sphere = parsePowerOptions(sphereAttribute?.values?.[0]?.name);

    // Return true only if both linder and sphere have valid values
    return (
      (linder === left.cylinder && sphere === left.sphere) ||
      (linder === right.cylinder && sphere === right.sphere)
    );
  });
};

export interface AddOnType {
  key: string;
  info: {
    addOnId: string;
    sku: LCSkuType;
    price: { net: number; gross: number };
    discount: { net: number; gross: number };
    undiscounted: { net: number; gross: number };
    tax: { type: string; amount: number };
    discountPercent?: number | null;
    availablePromotions?: AppliedPromotionsType[];
  };
}

export interface BundlePriceType {
  sku?: LCSkuType;
  price: string;
  offerPrice: string;
  offerPercent: string;
  tax: string;
  availablePromotions?: AppliedPromotionsType[];
}

/**
 * Lowest Item Price from Addons
 *
 * @param addOns
 * @param type
 * @returns
 */
export const getLowestPrice = (addOns: AddOnType[], type: string = "") => {
  const data = [...(type ? addOns.filter((a) => a.key.startsWith(type)) : addOns)];

  const lowest = data.reduce((lowest, current) => {
    if (current.info.price.gross === 0) {
      return lowest;
    }
    return current.info.price.gross < lowest.info.price.gross ? current : lowest;
  }, data[0]);

  return lowest;
};

/**
 * Parse Bundle Price Information
 *
 * @param addOns
 * @param addOnKey
 * @returns
 */
export const getBundlePrice = (addOns: AddOnType[], addOnKey: string): BundlePriceType => {
  if (addOnKey === "advanced") {
    const lowestAddOn = getLowestPrice(addOns);
    return {
      price: `${lowestAddOn?.info?.price.gross}`,
      offerPrice: "",
      offerPercent: "",
      tax: "",
    };
  }

  const bundle = addOns.find((item) => item.key === addOnKey);
  const { price, discount, undiscounted, tax, availablePromotions } = bundle?.info || {};
  return {
    sku: bundle?.info?.sku,
    price: discount?.gross
      ? `${toPrecise(undiscounted?.gross || 0, priceRoundOff)}`
      : `${price?.gross}`,
    offerPrice: discount?.gross ? `${toPrecise(price?.gross || 0, priceRoundOff)}` : "",
    offerPercent:
      discount?.gross && undiscounted?.gross ? formatDiscountPercentLabel(bundle?.info?.discountPercent) : "",
    tax: `${tax?.amount}`,
    availablePromotions,
  };
};

const normalizeBrandName = (name: string | undefined): string => (name ?? "").trim().toLowerCase();

/**
 * Resolve the bundle product ids configured for the frame's brand. A brand can map
 * to several bundles (one per lens index) — the gender/rim rules and the selected
 * index narrow them to one later. Empty when the brand has nothing configured, which
 * is what tells callers to use the default bundle instead.
 *
 * Accepts both CMS shapes: `bundles[]` and the single `bundle`.
 */
export const resolveBrandBundleIds = (
  brandBundles: BrandBundleRef[] | undefined,
  brandName: string | undefined,
): string[] => {
  const normalized = normalizeBrandName(brandName);
  const match = normalized
    ? brandBundles?.find((entry) => normalizeBrandName(entry.brand?.brandName) === normalized)
    : undefined;
  const bundles = hasArrayElements(match?.bundles || []) ? match?.bundles || [] : [match?.bundle];

  return bundles.map((item) => item?.id).filter((id): id is string => Boolean(id));
};

/**
 * Candidate product ids for a Lens Bundle card: the brand's bundles exclusively when
 * the frame's brand configures any, else the default. No mixing — a default from
 * another lens family would put the shopper on a different index.
 */
export const lensBundleCandidateIds = (
  bundle: LensBundleSectionItem,
  brandName: string | undefined,
): string[] => {
  const brandIds = resolveBrandBundleIds(bundle.brandBundles, brandName);
  return hasArrayElements(brandIds)
    ? brandIds
    : [bundle.id].filter((id): id is string => Boolean(id));
};

/**
 * Candidate product ids for an Additional Option, brand set kept separate from the
 * defaults: the brand's bundles win when Saleor serves them, the defaults stand in
 * when it doesn't.
 */
export const optionCandidateIds = (
  option: AdditionalOptionsSectionItem,
  brandName: string | undefined,
): { brandIds: string[]; defaultIds: string[] } => ({
  brandIds: resolveBrandBundleIds(option.brandBundles, brandName),
  defaultIds: (option.bundle || [])
    .map((item) => item.id)
    .filter((id): id is string => Boolean(id)),
});

/**
 * Cheapest AddOn among the given product ids. Ids absent from the pool are skipped —
 * they were filtered out (gender/rim, index) or Saleor never served them.
 */
export const cheapestById = (addOns: AddOnType[], ids: string[]): AddOnType | undefined =>
  ids
    .map((id) => addOns.find((a) => a.info.addOnId === id))
    .filter((a): a is AddOnType => Boolean(a))
    .reduce<AddOnType | undefined>(
      (lowest, current) =>
        !lowest || current.info.price.gross < lowest.info.price.gross ? current : lowest,
      undefined,
    );

/**
 * The product each Additional Option resolves to for this frame: the cheapest of the
 * brand's bundles, or the cheapest default when the brand has none available.
 */
export const resolveOptionAddOns = (
  options: AdditionalOptionsSectionItem[] | undefined,
  addOns: AddOnType[],
  brandName: string | undefined,
): Record<string, AddOnType> => {
  const resolved: Record<string, AddOnType> = {};
  (options || []).forEach((option) => {
    const { brandIds, defaultIds } = optionCandidateIds(option, brandName);
    const chosen = cheapestById(addOns, brandIds) || cheapestById(addOns, defaultIds);
    if (chosen) {
      resolved[option.key] = chosen;
    }
  });
  return resolved;
};

/**
 * Collect the Saleor product ids configured in Sanity (lens bundles + additional
 * options) so the configurator queries only the selected lens products instead of
 * the whole lens catalogue.
 *
 * Mirrors how each section picks its product. A lens bundle card takes the brand's
 * bundles exclusively when the brand configures any — a default from another lens
 * family would put the shopper on a different index — so the default is fetched only
 * when there is no brand match. An additional option keeps its defaults as a fallback,
 * so the option still renders when Saleor can't serve the brand bundle.
 *
 * @param pageData Sanity lens configurator page data
 * @param brandName Frame brand name
 * @returns distinct product ids, empty when Sanity has no bundles configured
 */
export const collectSelectedBundleIds = (
  pageData: Partial<LensConfigurationPageData> | undefined,
  brandName: string | undefined,
): string[] =>
  Array.from(
    new Set(
      [
        ...(pageData?.lensBundleSection || []).flatMap((bundle) =>
          lensBundleCandidateIds(bundle, brandName),
        ),
        ...(pageData?.additionalOptionSection || []).flatMap((option) => {
          const { brandIds, defaultIds } = optionCandidateIds(option, brandName);
          return [...brandIds, ...defaultIds];
        }),
      ].filter((id): id is string => Boolean(id)),
    ),
  );

/**
 * Parse Price properties from AddOn data for Display
 *
 * @param addOn
 * @returns
 */
export const parsePriceProperties = (
  addOn: AddOnType,
  baseAddOn: AddOnType | null = null,
): {
  price: string;
  offerPrice?: string;
  offerPercent?: string;
  tax?: string;
  availablePromotions?: AppliedPromotionsType[];
} => {
  const { price, discount, undiscounted, tax, availablePromotions } = addOn.info;

  if (baseAddOn) {
    return getPriceCompared(addOn.info, baseAddOn.info);
  }
  return {
    price: discount.gross ? `${undiscounted.gross}` : `${price.gross}`,
    offerPrice: discount.gross ? `${price.gross}` : "",
    offerPercent:
      discount.gross && undiscounted.gross ? formatDiscountPercentLabel(addOn.info.discountPercent) : "",
    tax: `${tax.amount}`,
    availablePromotions,
  };
};

export const getPriceCompared = (
  currentPriceInfo: Record<string, any>,
  comparedPriceInfo: Record<string, any>,
) => {
  const { price, undiscounted, discount, tax, availablePromotions } = currentPriceInfo;
  const { price: comparedPrice } = comparedPriceInfo;

  if (!comparedPrice?.gross) {
    return {
      price: discount.gross
        ? `${toPrecise(undiscounted.gross, priceRoundOff)}`
        : `${toPrecise(price.gross, priceRoundOff)}`,
      offerPrice: discount.gross ? `${toPrecise(price.gross, priceRoundOff)}` : "",
      offerPercent:
        discount.gross && undiscounted.gross ? formatDiscountPercentLabel(currentPriceInfo.discountPercent) : "",
      tax: `${tax.amount}`,
      availablePromotions,
    };
  }

  if (price.gross == comparedPrice.gross) {
    return {
      price: "INCLUDED",
      offerPrice: "",
      offerPercent: "",
      tax: "",
      availablePromotions,
    };
  }

  if (price.gross > comparedPrice?.gross) {
    const originalPrice = undiscounted.gross - comparedPrice.gross;
    const offerPrice = price.gross - comparedPrice.gross;
    const offerPercent = `-${toPrecise(((originalPrice - offerPrice) / originalPrice) * 100, percentileRoundOff)}%`;

    return {
      price: `${toPrecise(originalPrice, priceRoundOff)}`,
      offerPrice: "",
      offerPercent: "",
      tax: "",
      availablePromotions,
      ...(originalPrice !== offerPrice
        ? { offerPrice: `${offerPrice}`, offerPercent: `${offerPercent}` }
        : {}),
    };
  }

  if (price.gross < comparedPrice?.gross) {
    const originalPrice = toPrecise(price.gross - comparedPrice.gross, priceRoundOff);
    return {
      price: `${originalPrice}`,
      offerPrice: "",
      offerPercent: "",
      tax: "",
      availablePromotions,
    };
  }
  return {
    price: "",
    offerPrice: "",
    offerPercent: "",
    tax: "",
    availablePromotions,
  };
};

/**
 * Parse and populate customization property for the configurator
 *
 * @param addOnData
 * @param filters
 * @returns
 */
export const getCustomOptions = (
  addOnData: { key: any; info: any }[],
  filters: Record<string, string> | null = null,
): Record<string, any> => {
  const uniqueValues = {
    TREATMENT: new Set(),
    INDEX: new Set(),
    COATING: new Set(),
    DESIGN: new Set(),
  };

  const displayIndexAndPrice: Record<
    string,
    { index: string; price: string; offerPrice?: string; offerPercent?: string; tax?: string }
  > = {};

  addOnData?.forEach((a) => {
    const [DESIGN, INDEX, TREATMENT, COATING] = a.key.split(slugSeperator);
    uniqueValues.DESIGN.add(DESIGN.toUpperCase());
    uniqueValues.INDEX.add(INDEX.toUpperCase());
    uniqueValues.COATING.add(COATING.toUpperCase());
    uniqueValues.TREATMENT.add(TREATMENT.toUpperCase());

    const displayIndex = displayIndexAndPrice[DESIGN.toUpperCase()];

    if (filters?.[DESIGN]) {
      const { DESIGN: _filterDesignValue, INDEX: filterIndexValue } = unsluggify(filters[DESIGN]);

      if (filterIndexValue === INDEX) {
        if (!displayIndex || displayIndex?.price > a.info.price.gross) {
          displayIndexAndPrice[DESIGN.toUpperCase()] = {
            index: INDEX.toUpperCase(),
            ...parsePriceProperties(a),
          };
        }
      }
    } else if (!displayIndex || displayIndex?.price > a.info.price.gross) {
      displayIndexAndPrice[DESIGN.toUpperCase()] = {
        index: INDEX.toUpperCase(),
        ...parsePriceProperties(a),
      };
    }
  });

  return {
    uniqueValues: {
      TREATMENT: Array.from(uniqueValues.TREATMENT) as string[],
      INDEX: Array.from(uniqueValues.INDEX) as string[],
      COATING: Array.from(uniqueValues.COATING) as string[],
      DESIGN: Array.from(uniqueValues.DESIGN) as string[],
    },
    displayIndexAndPrice,
  };
};

/**
 * Slug from options
 *
 * @param design
 * @param index
 * @param coating
 * @param treatment
 * @returns
 */
export const sluggify = (
  design: string = "",
  index: string = "",
  coating: string = "",
  treatment: string = "",
) => {
  return `${design}${slugSeperator}${index}${slugSeperator}${treatment}${slugSeperator}${coating}`;
};

/**
 * Option-Choice map from slug
 *
 * @param slug
 * @returns
 */
export const unsluggify = (slug: string): Record<string, string> => {
  const [DESIGN, INDEX, TREATMENT, COATING] = slug.split(slugSeperator);
  return {
    ...(DESIGN ? { DESIGN } : {}),
    ...(INDEX ? { INDEX } : {}),
    ...(TREATMENT ? { TREATMENT } : {}),
    ...(COATING ? { COATING } : {}),
  };
};

/**
 * Attribute map to slug
 *
 * @param a
 * @returns
 */
export const attributeMaptoString = (a: Record<string, string>) => {
  return `${a.DESIGN}${slugSeperator}${a.INDEX}${slugSeperator}${a.TREATMENT}${slugSeperator}${a.COATING}`;
};

/**
 * Transform the raw filters to the format required by configurator API
 *
 * @param rawFilters
 * @returns
 */
export const transformFilters = (rawFilters?: Record<string, any>) => {
  if (!rawFilters) {
    return {
      brands: defaultFilters.brands,
      attributes: defaultFilters.attributes,
    };
  }
  const { CUSTOMIZATION } = rawFilters;
  const { DESIGN, INDEX, TREATMENT, COATING } = CUSTOMIZATION || {};

  return {
    brands: defaultFilters.brands,
    attributes: [
      ...defaultFilters.attributes,
      ...(DESIGN ? [{ slug: propertySlugs.DESIGN, values: [DESIGN] }] : []),
      ...(INDEX ? [{ slug: propertySlugs.INDEX, values: [INDEX] }] : []),
      ...(TREATMENT ? [{ slug: propertySlugs.TREATMENT, values: [TREATMENT] }] : []),
      ...(COATING ? [{ slug: propertySlugs.COATING, values: [COATING] }] : []),
    ],
  };
};

/**
 * Creating a choice filter for the selected options
 * @param options
 * @param pv
 * @returns
 */
export const optionsToChoiceFilter = (
  options: { DESIGN?: string; INDEX?: string; TREATMENT?: string; COATING?: string },
  pv: Record<string, string>,
) => {
  if (!options.DESIGN || !options.INDEX) return {};

  return {
    ...pv,
    [options.DESIGN]: sluggify(options.DESIGN, options.INDEX),
  };
};

/**
 * Populate the choice summary for Bundles (with the Design and Index display titles)
 *
 * @param bundle
 * @param lensOptions
 * @param indexOptions
 * @returns
 */
export const populateChoiceSummary = (
  bundle: string,
  lensOptions: LensOptionsSectionItem[],
  indexOptions: LensIndexItem[],
) => {
  const { DESIGN, INDEX } = unsluggify(bundle);
  const designStaticInfo = lensOptions?.find(
    (item) => item.type === lensDesignValues?.[DESIGN?.toUpperCase()],
  );
  const indexStaticInfo = indexOptions?.find(
    (item) => item.index === lensIndexValue?.[INDEX || ""] || "",
  );
  if (designStaticInfo && indexStaticInfo) {
    return {
      ...(DESIGN ? { DESIGN: { key: DESIGN, title: designStaticInfo.title } } : {}),
      ...(INDEX ? { INDEX: { key: INDEX, title: indexStaticInfo.title } } : {}),
    };
  }
  return {};
};

export const currentIndexParser = (
  currentStep: lensConfigurationSteps,
  steps: lensConfigurationSteps[],
) => {
  const indexedSteps = Object.keys(indexParser);

  if (indexedSteps.includes(currentStep)) {
    return steps.indexOf(indexParser[currentStep]);
  }

  return steps.indexOf(currentStep);
};

export const getPreviousStep = (
  currentStep: lensConfigurationSteps,
  steps: lensConfigurationSteps[],
  skippedSteps: lensConfigurationSteps[] = [],
) => {
  const actualSteps = skippedSteps ? steps.filter((step) => !skippedSteps.includes(step)) : steps;
  return actualSteps[actualSteps.indexOf(currentStep) - 1];
};

export const getAddOns = (
  lensConfigurators: LensConfigs,
  product?: ConfiguratorProductInfo,
  step?: lensConfigurationSteps,
  opticalData?: OpticalData, // Note: The api is not returning a single variant now properly. So, the opticalData is passed to get the proper sku
): AddOnType[] => {
  const addOnItems: LensConfiguratorNode[] = mapEdgesToItems(lensConfigurators || {});

  const addOns = addOnItems.map((addon: LensConfiguratorNode) => {
    return {
      key: parseAttributes(addon.attributes),
      info: { ...parseInfo(addon.variants, opticalData), addOnId: addon.id },
    };
  });

  return businessFilter(addOns, product, step);
};

export const businessFilter = (
  addOns: AddOnType[],
  product?: ConfiguratorProductInfo,
  step?: lensConfigurationSteps,
) => {
  if (!product || !step) {
    return addOns;
  }
  return disableBusinessRules ? addOns : addOnFilter(product, addOns, customFilters[step]);
};

export const getLowestTransitionPrice = (
  transitions: {
    price: string;
    offerPrice: string;
    offerPercent: string;
    tax: string;
    slug: string;
    sku: LCSkuType;
  }[],
): {
  price: string;
  offerPrice: string;
  offerPercent: string;
  tax: string;
  slug: string;
  sku: LCSkuType;
} => {
  return transitions.reduce((lowest, current) => {
    const comparingLowest = lowest.offerPrice || lowest.price;
    const comparedPrice = current.offerPrice || current.price;
    return comparedPrice < comparingLowest ? current : lowest;
  }, transitions[0]);
};

export const translate = (key: Record<string, string>, lang: string) => {
  return key[lang] || key["en"];
};

export const parseAndTranslate = (key: string, lang: string) => {
  let obj = {};
  try {
    obj = JSON.parse(key);
    return translate(obj, lang);
  } catch (e) {
    return key;
  }
};

const parse = (key: string) => {
  try {
    return JSON.parse(key);
  } catch (e) {
    Sentry.captureException(e);
    return {};
  }
};

export const parseMeta = (
  meta: SaleorMetadata[],
  lang: string,
): Record<
  string,
  { key: string; option: string; choice: string; subchoice: string; color?: string }
> => {
  const prasedData: Record<
    string,
    { key: string; option: string; choice: string; subchoice: string; color?: string }
  > = {};
  meta?.forEach((item) => {
    if (item.key === metadataKeys.PRESCRIPTION) {
      if (item.value === metadataValues.SEND_LATER) {
        prasedData[item.key] = {
          key: item.key,
          option: item.key,
          choice: item.value,
          subchoice: "",
        };
      } else {
        const data = parse(item.value);
        prasedData[item.key] = {
          key: item.key,
          option: item.key,
          choice: data.source,
          subchoice: data.title,
        };
      }
    } else if (item.key === metadataKeys.ADDITIONAL_OPTIONS) {
      const addon = parse(item.value);
      prasedData[item.key] = {
        key: item?.key,
        option: item?.key,
        choice: addon?.option?.[lang] || "",
        subchoice: addon?.color?.displayValue?.[lang] || "",
        color: addon?.color?.value || "",
      };
    } else {
      prasedData[item.key] = {
        key: item.key,
        option: item.key,
        choice: parseAndTranslate(item.value, lang),
        subchoice: "",
      };
    }
  });
  return prasedData;
};

export const getItemMeta = (
  summary: LensSummaryItemType[],
  sendLater: boolean,
  prescription: PrescriptionData,
): SaleorMetadata[] => {
  const lensType = summary.find((item) => item.key === lensConfigurationSteps.LENS_TYPE);
  const lensCustomization = summary.find(
    (item) => item.key === lensConfigurationSteps.LENS_CUSTOMIZATION,
  );
  const additionalOption = summary.find(
    (item) => item.key === lensConfigurationSteps.ADDITIONAL_OPTIONS,
  );

  return [
    {
      key: metadataKeys.LENS_TYPE,
      value: JSON.stringify(removeTypeField(lensType?.choices?.[0]?.displayValue)) || "",
    },
    {
      key: metadataKeys.LENS,
      value:
        JSON.stringify(
          removeTypeField(
            lensCustomization?.choices?.find((choice) => choice.key === "DESIGN")?.displayValue,
          ),
        ) || "",
    },
    ...(additionalOption
      ? [
          {
            key: metadataKeys.ADDITIONAL_OPTIONS,
            value:
              JSON.stringify({
                option: removeTypeField(additionalOption?.choices?.[0]?.displayValue),
                color: {
                  value: additionalOption?.choices?.[1]?.value,
                  displayValue: additionalOption?.choices?.[1]?.displayValue,
                },
              }) || "",
          },
        ]
      : []),
    ...(sendLater
      ? [
          {
            key: metadataKeys.PRESCRIPTION,
            value: metadataValues.SEND_LATER,
          },
        ]
      : [
          ...(prescription
            ? [
                {
                  key: metadataKeys.PRESCRIPTION,
                  value: JSON.stringify({
                    id: prescription.id,
                    title: prescription.title,
                    source: prescription.newPrescription
                      ? metadataValues.UPLOADED
                      : metadataValues.SAVED,
                  }),
                },
              ]
            : []),
        ]),
  ];
};

const removeTypeField = (obj?: Record<any, any>) => {
  if (!obj) return obj;
  const { _type, ...rest } = obj;
  return rest;
};

export const getSkuMeta = (sku: LCSkuType, prescription?: PrescriptionData): LCSkuType => {
  const { left: leftEye, right: rightEye } = sku;
  if (!prescription) {
    return {
      left: { ...leftEye, metadata: [{ key: metadataKeys.EYE, value: metadataValues.OS }] },
      right: { ...leftEye, metadata: [{ key: metadataKeys.EYE, value: metadataValues.OD }] },
    };
  }
  const { left: prescriptionLeft } = prescription?.opticalData || {};
  const { left } = parseSKU(sku);

  const cl =
    left.attributes?.find((a) => a.name === lensPowerAttributeNames.CYLINDRICAL)?.value || 0;
  const sl = left.attributes?.find((a) => a.name === lensPowerAttributeNames.SPHERICAL)?.value || 0;

  if (prescriptionLeft?.cylinderPower !== cl && prescriptionLeft?.sphericalPower !== sl) {
    return {
      left: { ...rightEye, metadata: [{ key: metadataKeys.EYE, value: metadataValues.OS }] },
      right: { ...leftEye, metadata: [{ key: metadataKeys.EYE, value: metadataValues.OD }] },
    };
  }
  return {
    left: { ...rightEye, metadata: [{ key: metadataKeys.EYE, value: metadataValues.OS }] },
    right: { ...leftEye, metadata: [{ key: metadataKeys.EYE, value: metadataValues.OD }] },
  };
};

function parseSKU(sku: LCSkuType) {
  const left = {
    id: sku.left?.id,
    attributes: sku.left?.attributes?.map((attribute) => ({
      name: attribute.attribute?.[attributeNameProperty],
      value: attribute.values[0]?.[attributeValueProperty],
    })),
  };

  return { left };
}

export const parseOpticalData = (item: CustomerPrescriptionNode) => {
  const prescription = {
    left: {
      ...(item.leftAxis ? { axis: item.leftAxis } : {}),
      ...(item.leftCylinderPower ? { cylinderPower: item.leftCylinderPower } : {}),
      ...(item.leftPupilSize ? { pupilSize: item.leftPupilSize } : {}),
      ...(item.leftSphericalPower ? { sphericalPower: item.leftSphericalPower } : {}),
    },
    right: {
      ...(item.rightAxis ? { axis: item.rightAxis } : {}),
      ...(item.rightCylinderPower ? { cylinderPower: item.rightCylinderPower } : {}),
      ...(item.rightPupilSize ? { pupilSize: item.rightPupilSize } : {}),
      ...(item.rightSphericalPower ? { sphericalPower: item.rightSphericalPower } : {}),
    },
  };
  return hasArrayElements(Object.values(prescription.left)) &&
    hasArrayElements(Object.values(prescription.right))
    ? prescription
    : undefined;
};

export const checkConfigIsWellFormed = (addOns: AddOnType[]) => {
  // Add conditions and checks to do a basic sanity of the added configuration
  let configIsWellFormed = true;
  if (!hasArrayElements(addOns)) {
    Sentry.captureMessage("0f2e0e61-86c1-413d-9a83-dc2c9497214e: OL's are absent");
    configIsWellFormed = false;
  }

  const addOnSample = addOns?.[0];
  if (addOnSample) {
    Sentry.captureMessage(
      `3041aaa8-b592-4dfb-86a7-7bb88c665d46: OL's key pattern: ${addOnSample?.key || "missing"}`,
    );
  }

  return configIsWellFormed;
};

export const populateTransitionOptions = (
  optionsAndPrices: Record<
    string,
    {
      price: string;
      offerPrice: string;
      offerPercent: string;
      tax: string;
      slug: string;
      sku: LCSkuType;
      availablePromotions?: AppliedPromotionsType[];
    }
  >,
  transitionStaticInfo?: TransitionItemType[],
) => {
  const transitionPrices = transitionStaticInfo?.map((op) => optionsAndPrices[op.slug]) || [];
  return {
    transitionOptions: transitionStaticInfo?.map((transition) => {
      return {
        ...transition,
        ...optionsAndPrices[transition.slug],
      };
    }),
    ...getLowestTransitionPrice(transitionPrices),
  };
};

const expresso = (a: string | boolean | null, operator: string = "", c: string = "") => {
  switch (operator) {
    case "==":
      return a === c;
    case "!=":
      return a !== c;
    default:
      return false;
  }
};

export const addOnFilterV2 = (
  product: ConfiguratorProductInfo,
  addOns: AddOnType[],
  filters: FilterType[],
): AddOnType[] => {
  if (!hasArrayElements(filters)) {
    return addOns;
  }

  const [filter] = filters;
  const { attribute, operator, value, inclusion, exclusion } = filter;
  const productAttributeValue = getAttributeValue(product.attributes, attribute);
  let filtered = addOns;

  if (value.some((val) => expresso(productAttributeValue, operator, val))) {
    filtered = addOns.filter((addOn) => {
      if (inclusion.length > 0) {
        return inclusion.some((inc) => {
          const slugObj = unsluggify(addOn.key);
          return slugObj?.[inc.attribute] === inc.value;
        });
      }
      if (exclusion.length > 0) {
        return !exclusion.some((exc) => {
          const slugObj = unsluggify(addOn.key);
          return slugObj?.[exc.attribute] === exc.value;
        });
      }
    });
  }
  return addOnFilterV2(product, filtered, filters.slice(1));
};

export const addOnFilter = (
  product: ConfiguratorProductInfo,
  addOns: AddOnType[],
  filters: FilterType[],
): AddOnType[] => {
  if (!filters) {
    return addOns;
  }
  const gender = getAttributeValue(product.attributes, "genderEnum") as string;
  const frameType = getAttributeValue(product.attributes, "frameTypeEnum") as string;

  const kids = ["kids", "Kids", "girl", "Girl", "girls", "Girls", "boy", "Boy", "boys", "Boys"];
  const adults = [
    "men",
    "Men",
    "male",
    "Male",
    "female",
    "Female",
    "women",
    "Women",
    "unisex",
    "Unisex",
  ];

  const rimless = ["rimless", "half-rim", "half_rim"];
  const rimmed = ["full_rim", "full-rim"];

  return addOns.filter((addOn) => {
    if (kids.includes(gender) && rimless.includes(frameType)) {
      // kids and rimless
      const slugObj = unsluggify(addOn.key);
      return slugObj?.["INDEX"] === "16-millimeter" || slugObj?.["INDEX"] === "159-millimeter";
    }
    if (kids.includes(gender) && rimmed.includes(frameType)) {
      // kids and rimmed
      const slugObj = unsluggify(addOn.key);
      return slugObj?.["INDEX"] === "16-millimeter" || slugObj?.["INDEX"] === "159-millimeter";
    }
    if (adults.includes(gender) && rimless.includes(frameType)) {
      // adults and rimless
      const slugObj = unsluggify(addOn.key);
      return slugObj?.["INDEX"] === "16-millimeter" || slugObj?.["INDEX"] === "159-millimeter";
    }
    if (adults.includes(gender) && rimmed.includes(frameType)) {
      // adults and rimmed
      // const slugObj = unsluggify(addOn.key);
      // return slugObj?.["INDEX"] === "15-millimeter"

      // return slugObj?.["INDEX"] === "156-millimeter" || slugObj?.["INDEX"] === "15-millimeter";
      return true;
    }
  });
};

export const getCurrentDateTimeForLog = (): string => {
  const date = new Date();
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: true,
  };

  return date.toLocaleString("en-IN", options);
};

export const generateProductInfo = (
  product: Product,
  variant?: VariantItemPdp,
  language: string = "en",
): ConfiguratorProductInfo => {
  const raw = getAttributeValue(variant?.attributes ?? [], "bundle_id", null, language);
  const bundleIds = typeof raw === "string" && raw.trim() ? raw.split(",").map(s => s.trim()).filter(Boolean) : [];
  const isBundle = bundleIds.length > 0;
  
  // Same as PDP / cart: strikethrough uses RRP (costPrice when > list); sale uses payable
  const salePrice =
    (variant?.pricing?.price?.gross?.amount ||
      variant?.pricing?.priceUndiscounted?.gross?.amount) ??
    0;
  const listPrice = getVariantRrpAmount(variant?.pricing);

  return {
    name: product.name,
    category: product.category?.slug ?? "",
    productId: product.id,
    variantId: variant?.id ?? "",
    variantName: variant?.name ?? "",
    attributes: [
      ...(product?.attributes && hasArrayElements(product.attributes) ? product?.attributes : []),
      ...(variant?.attributes && hasArrayElements(variant?.attributes) ? variant?.attributes : []),
    ],
    brand: product.brand?.name ?? "",
    image: getVariantImages(variant?.attributes, language)?.[0] ?? "",
    isBundle,
    price: {
      currency: variant?.pricing?.price?.currency ?? variant?.pricing?.price?.gross?.currency ?? "",
      salePrice,
      listPrice,
      tax: variant?.pricing?.price?.tax?.amount ?? 0,
    },
  };
};

export const transformMetaToSummary = (
  meta: SaleorMetadata[],
  stepTitles: Record<string, string>,
): LensSummaryItemType[] => {
  const summary: LensSummaryItemType[] = [];

  meta?.forEach((item) => {
    if (item.key === metadataKeys.LENS_TYPE) {
      const lensTypeData = JSON.parse(item.value || "{}");
      summary.push({
        key: lensConfigurationSteps.LENS_TYPE,
        option: stepTitles[lensConfigurationSteps.LENS_TYPE],
        choices: [{ value: lensTypeData.value, displayValue: lensTypeData }],
      });
    }

    if (item.key === metadataKeys.LENS) {
      const lensCustomizationData = JSON.parse(item.value || "{}");
      const choices: ChoiceType[] = [
        { key: "DESIGN", value: lensCustomizationData.value, displayValue: lensCustomizationData },
      ];
      summary.push({
        key: lensConfigurationSteps.LENS_CUSTOMIZATION,
        option: stepTitles[lensConfigurationSteps.LENS_CUSTOMIZATION],
        choices,
      });
    }

    if (item.key === metadataKeys.ADDITIONAL_OPTIONS) {
      const additionalOptionsData = JSON.parse(item.value || "{}");
      const choices: ChoiceType[] = [
        {
          value: additionalOptionsData.option.value,
          displayValue: additionalOptionsData.option.displayValue,
        },
      ];

      if (additionalOptionsData.color) {
        choices.push({
          key: "COLOR",
          value: additionalOptionsData.color.value,
          displayValue: additionalOptionsData.color.displayValue,
        });
      }

      summary.push({
        key: lensConfigurationSteps.ADDITIONAL_OPTIONS,
        option: stepTitles[lensConfigurationSteps.ADDITIONAL_OPTIONS],
        choices,
      });
    }

    if (item.key === metadataKeys.PRESCRIPTION) {
      if (item.value === metadataValues.SEND_LATER) {
        summary.push({
          key: lensConfigurationSteps.PRESCRIPTION,
          option: stepTitles[lensConfigurationSteps.PRESCRIPTION],
          choices: [
            { value: metadataValues.SEND_LATER, displayValue: { en: metadataValues.SEND_LATER } },
          ],
        });
      } else {
        const prescriptionData = JSON.parse(item.value || "{}");
        summary.push({
          key: lensConfigurationSteps.PRESCRIPTION,
          option: stepTitles[lensConfigurationSteps.PRESCRIPTION],
          choices: [
            {
              value: prescriptionData.source,
              displayValue: { en: prescriptionData.title },
            },
          ],
        });
      }
    }
  });

  return summary;
};

export const transformPrescriptions = (prescriptionsRaw: CustomerPrescriptions) => {
  return mapEdgesToItems(prescriptionsRaw).map((item: CustomerPrescriptionNode) => ({
    key: item.id,
    title: item.title,
    source: item.source,
    date: formatDate(item.createdDate),
    opticalData: parseOpticalData(item),
    images: item.images,
  }));
};
